import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

export interface RemoveConfirmationDialogProps {
  open: boolean;
  title: string;
  /** Body content (stack your own Typography / layout inside). */
  children: ReactNode;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  /** Shown on the confirm button while `isPending` (default: "Removing…"). */
  pendingConfirmLabel?: string;
  cancelTestId?: string;
  confirmTestId?: string;
  /** Optional `id` on the title node (e.g. for `aria-labelledby`). */
  titleId?: string;
  /** Optional `id` on the content region (e.g. for `aria-describedby`). */
  contentId?: string;
}

/** Modal to confirm removing an entity (fixture, reel option, library asset, etc.). */
export const RemoveConfirmationDialog = ({
  open,
  title,
  children,
  isPending,
  onCancel,
  onConfirm,
  confirmLabel,
  pendingConfirmLabel = 'Removing…',
  cancelTestId,
  confirmTestId,
  titleId,
  contentId,
}: RemoveConfirmationDialogProps) => {
  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (isPending) return;
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      aria-describedby={contentId}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent dividers id={contentId}>
        {children}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isPending}
          data-testid={cancelTestId}
        >
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={isPending}
          data-testid={confirmTestId}
        >
          {isPending ? pendingConfirmLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
